import { useNavigationStore } from '@/stores/navigationStore';
import { Address, StreetBreakdown, Voter } from '@/types/canvassing';
import { BattlePlan, Engagement } from '@/types/engagement';
import { PollingStation, PollingStationInfo } from '@/types/polling_station';
import { authenticatedFetch } from '@/utils/auth';
import { createPlatformStorage } from '@/utils/indexedDBStorage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface EngagementMapDetails {
  engagement_id: string;
  geometry: any; // GeoJSON geometry
  last_updated: string;
}

export interface PostcodeSlicePolygons {
  engagement_id: string;
  polygons: any[]; // Array of GeoJSON polygons
  last_updated: string;
}

export interface VIStats {
  total_voters: number;
  total_addresses: number;
  total_phone_numbers: number;
  total_voters_with_vis: number;
  total_households_with_vis: number;
  vi_counts: {
    [key: string]: number;
  };
}

// Remove the separate addresses array since it's now part of EngagementMapDetails

export interface CampaignStore {
  // Current context (synced from navigation store)
  selectedBranch: string | null;
  selectedCampaign: string | null;
  
  // Cached data - organized by branch and campaign
  cachedData: {
    [branchId: string]: {
      // Campaign-specific data
      campaigns: {
        [campaignId: string]: {
          // Battle plans for this campaign
          battlePlans: BattlePlan[];
          
          // Engagements for each battle plan
          engagements: {
            [battlePlanId: string]: Engagement[];
          };
          
          // Polling stations for each battle plan (telling)
          pollingStations: {
            [battlePlanId: string]: PollingStation[];
          };
          
          // Map details for each engagement
          engagementMapDetails: {
            [engagementId: string]: EngagementMapDetails;
          };
          
          // Addresses for each engagement
          addresses: {
            [engagementId: string]: Address[];
          };
          
          // Street breakdown for each engagement
          streetBreakdowns: {
            [engagementId: string]: StreetBreakdown[];
          };
          
          // Postcode slice polygons for each engagement
          postcodeSlicePolygons: {
            [engagementId: string]: PostcodeSlicePolygons;
          };
          
          // Polling station info for each engagement
          pollingStationInfo: {
            [engagementId: string]: PollingStationInfo;
          };
          
          // Telling voters by engagement IDs (composite key for polling station efficiency)
          tellingVotersByEngagements: {
            [engagementIdsKey: string]: { // Format: "engagementId1,engagementId2,engagementId3" (sorted)
              voters: any[];
              last_updated: string;
            };
          };
          
          // Voter and address updates (offline changes)
          voterUpdates: {
            [voterId: string]: {
              latest_vi?: string;
              latest_vi_datetime?: string;
              key_issues?: string;
              phone_number?: string;
              email?: string;
              reform_window_poster?: string;
              reform_garden_board?: string;
              // GOTV fields
              voted_yet?: boolean;
              will_not_vote?: boolean;
              confirmation_type?: string;
              voting_confirmation_timestamp?: string;
              has_been_contacted?: boolean;
              gotv_notes?: string;
              will_vote_by_time?: string;
              requested_lift?: boolean;
              requested_lift_preferred_windows?: string;
              // Additional fields for sync service
              address_id?: string;
              engagement_id?: string;
              updates?: { _timestamp: number; _sequence: number; [key: string]: any }[];
            };
          };
          addressUpdates: {
            [addressId: string]: {
              not_in?: boolean;
              engagement_id?: string;
              updates?: { _timestamp: number; _sequence: number; [key: string]: any }[];
            };
          };
          // Telling updates (offline changes for telling engagements)
          tellingUpdates: {
            [voterId: string]: {
              voted_yet?: boolean;
              engagement_id?: string;
              voting_confirmation_timestamp?: string;
              confirmation_type?: string;
              updates?: { _timestamp: number; _sequence: number; [key: string]: any }[];
            };
          };
          // Persistent interaction IDs
          interactionIds: {
            [addressId: string]: {
              interactionId: string;
              timestamp: number; // Unix timestamp in milliseconds
              notIn: boolean; // Whether the interaction was created with not_in flag
            };
          };
          // Persistent telling interaction IDs
          tellingInteractionIds: {
            [key: string]: {
              interactionId: string;
              timestamp: number; // Unix timestamp in milliseconds
              voterId: string; // The voter ID associated with this interaction
              engagementId: string; // The engagement ID for this interaction
            };
          };
          
          // VI stats for the campaign
          viStats?: VIStats | null;
        };
      };
      // Branch-level data (not campaign-specific)
      myActiveEngagements?: Engagement[];
    };
  };
  
  // Actions
  syncWithNavigationStore: () => void;
  
  // Data fetching and caching
  fetchBattlePlans: () => Promise<BattlePlan[]>;
  getBattlePlans: () => BattlePlan[] | null;
  fetchEngagements: (battlePlanId: string, filterByElectoralDivisionIds?: boolean) => Promise<Engagement[] | null>;
  getEngagements: (battlePlanId: string) => Engagement[] | null;
  fetchEngagementInfo: (engagementId: string, battlePlanId: string, branchId?: string, campaignId?: string) => Promise<Engagement | null>;
  fetchMyActiveEngagements: () => Promise<Engagement[]>;
  getMyActiveEngagements: () => Engagement[];
  fetchPollingStations: (battlePlanId: string) => Promise<PollingStation[] | null>;
  getPollingStations: (battlePlanId: string) => PollingStation[] | null;
  fetchEngagementMapDetails: (engagementIds: string[], battlePlanId: string) => Promise<{ [engagementId: string]: EngagementMapDetails }>;
  getEngagementMapDetails: (engagementId: string) => EngagementMapDetails | null;
  fetchAddressesForEngagement: (engagementId: string, branchId?: string, campaignId?: string, isGotv?: boolean) => Promise<Address[]>;
  getAddressesForEngagement: (engagementId: string) => Address[];
  fetchEngagementStreetBreakdown: (engagementId: string, branchId?: string, campaignId?: string) => Promise<StreetBreakdown[]>;
  getEngagementStreetBreakdown: (engagementId: string) => StreetBreakdown[];
  fetchPostcodeSlicePolygons: (engagementId: string, branchId?: string, campaignId?: string) => Promise<PostcodeSlicePolygons | null>;
  getPostcodeSlicePolygons: (engagementId: string) => PostcodeSlicePolygons | null;
  fetchTellingVotersByEngagements: (engagementIds: string[] | null) => Promise<any[] | null>;
  getTellingVotersByEngagements: (engagementIds: string[]) => any[] | null;
  fetchPollingStationInfo: (engagementId: string, branchId?: string, campaignId?: string) => Promise<PollingStationInfo | null>;
  getPollingStationInfo: (engagementId: string) => PollingStationInfo | null;
  fetchVIStats: () => Promise<VIStats | null>;
  getVIStats: () => VIStats | null;
  
  // Existing actions
  updateVoterData: (voterId: string, updates: Partial<Voter>) => void;
  updateAddressData: (addressId: string, updates: Partial<Address>) => void;
  updateTellingData: (voterId: string, updates: any) => void;
  getVoterUpdates: (voterId: string) => any;
  getAddressUpdates: (addressId: string) => any;
  getTellingUpdates: (voterId: string) => any;
  clearCurrentContext: () => void;
  clearAllData: () => void;
  clearEngagementData: (engagementId: string) => void;
  storeInteractionId: (addressId: string, interactionId: string, notIn: boolean) => void;
  storeTellingInteractionId: (engagementId: string, interactionId: string, voterId: string) => void;
}

export const useCampaignStore = create<CampaignStore>()(
  persist(
    (set, get) => {
      // Helper function to initialize campaign data
      const initializeCampaign = (branchId: string, campaignId: string) => {
        const state = get();
        const newCachedData = { ...state.cachedData };
        
        // Initialize branch if it doesn't exist
        if (!newCachedData[branchId]) {
          newCachedData[branchId] = {
            campaigns: {},
            myActiveEngagements: []
          };
        }
        
        // Initialize campaigns object if it doesn't exist
        if (!newCachedData[branchId].campaigns) {
          newCachedData[branchId].campaigns = {};
        }
        
        // Initialize campaign if it doesn't exist
        if (!newCachedData[branchId].campaigns[campaignId]) {
          newCachedData[branchId].campaigns[campaignId] = {
            addresses: {},
            streetBreakdowns: {},
            postcodeSlicePolygons: {},
            pollingStationInfo: {},
            tellingVotersByEngagements: {},
            battlePlans: [],
            engagements: {},
            pollingStations: {},
            engagementMapDetails: {},
            voterUpdates: {},
            addressUpdates: {},
            tellingUpdates: {},
            interactionIds: {},
            tellingInteractionIds: {},
            viStats: null
          };
        }
        
        return { 
          newCachedData,
          campaignData: newCachedData[branchId].campaigns[campaignId]
        };
      };

      return {
                // Initial state
        selectedBranch: null,
        selectedCampaign: null,
        cachedData: {},

        // Sync with navigation store
        syncWithNavigationStore: () => {
          const navigationStore = useNavigationStore.getState();
          const branch = navigationStore.getSelectedBranch();
          const campaign = navigationStore.getSelectedCampaign();
          
          set({
            selectedBranch: branch?.branch_short_name || null,
            selectedCampaign: campaign?.boundary_id || null
          });
        },

        // Fetch battle plans from API and cache them
      fetchBattlePlans: async () => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          throw new Error('No current context set for battle plans');
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/canvassing/get-battle-plans',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                electoral_division_ids: [selectedCampaign]
              }),
            },
            'groundforce'
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const battlePlans: BattlePlan[] = await response.json();
          
          // Cache the battle plans
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            // Set battle plans
            campaignData.battlePlans = battlePlans;

            return { cachedData: newCachedData };
          });

        return battlePlans;
      } catch (error) {
        console.warn('Error fetching battle plans (may be expected during background fetch):', error);
        throw error;
      }
    },

      getBattlePlans: () => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.battlePlans || null;
      },

      // Fetch engagements from API and cache them
      fetchEngagements: async (battlePlanId: string, filterByElectoralDivisionIds: boolean = false) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch) {
          throw new Error('No current context set for engagements');
        }

        if (battlePlanId === '') {
          return null;
        }

        const requestBody: { battle_plan_id: string; electoral_division_ids?: string[] } = {
          battle_plan_id: battlePlanId
        };

        // Only include electoral_division_ids if requested and selectedCampaign is available
        if (filterByElectoralDivisionIds && selectedCampaign) {
          requestBody.electoral_division_ids = [selectedCampaign];
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/canvassing/get-engagements',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody),
            },
            'groundforce'
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const engagements: Engagement[] = await response.json();
          
          // Cache the engagements (only if selectedCampaign is available)
          if (selectedCampaign) {
            set(() => {
              const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

              // Set engagements for this battle plan
              campaignData.engagements[battlePlanId] = engagements;

              return { cachedData: newCachedData };
            });
          }

          return engagements;
        } catch (error) {
          console.warn('Error fetching engagements (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Cache management for engagements
      setEngagements: (battlePlanId: string, engagements: Engagement[]) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for engagements');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Set engagements for this battle plan
          campaignData.engagements[battlePlanId] = engagements;

          return { cachedData: newCachedData };
        });
      },

      getEngagements: (battlePlanId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.engagements[battlePlanId] || null;
      },

      // Fetch polling stations for a battle plan from the API
      fetchPollingStations: async (battlePlanId: string) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          throw new Error('No current context set for polling stations');
        }

        if (battlePlanId === '') {
          return null;
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/telling/get-polling-stations',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                battle_plan_id: battlePlanId
              }),
            },
            'groundforce'
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const pollingStations: PollingStation[] = await response.json();
          
          // Cache the polling stations
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            if (!campaignData.pollingStations) {
              campaignData.pollingStations = {};
            }
            
            campaignData.pollingStations[battlePlanId] = pollingStations;
            
            return { cachedData: newCachedData };
          });

          return pollingStations;
        } catch (error) {
          console.error('Error fetching polling stations:', error);
          throw error;
        }
      },

      getPollingStations: (battlePlanId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.pollingStations?.[battlePlanId] || null;
      },

      // Fetch specific engagement info from API and update it in the store
      fetchEngagementInfo: async (engagementId: string, battlePlanId: string, branchId?: string, campaignId?: string) => {
        // Use provided branch/campaign IDs or fall back to current context
        let targetBranchId = branchId;
        let targetCampaignId = campaignId;
        
        if (!targetBranchId || !targetCampaignId) {
          const { selectedBranch, selectedCampaign } = get();
          targetBranchId = targetBranchId || selectedBranch || undefined;
          targetCampaignId = targetCampaignId || selectedCampaign || undefined;
        }
        
        if (!targetBranchId || !targetCampaignId) {
          console.warn('No context set for engagement info - this may be expected during background fetch');
          throw new Error('No context set for engagement info');
        }

        try {
          const response = await authenticatedFetch(
            targetBranchId,
            '/canvassing/get-engagements',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                battle_plan_id: battlePlanId,
                engagement_ids: [engagementId]
              }),
            },
            'groundforce'
          );

          if (!response.ok) {
            console.warn(`Engagement info fetch failed with status ${response.status} - this may be expected during background fetch`);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const engagements: Engagement[] = await response.json();
          
          if (engagements.length === 0) {
            console.warn(`No engagement found with ID ${engagementId}`);
            return null;
          }

          const updatedEngagement = engagements[0];
          
          // Update the specific engagement in the store
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(targetBranchId, targetCampaignId);

            // Get existing engagements for this battle plan
            const existingEngagements = campaignData.engagements[battlePlanId] || [];
            
            // Find and replace the specific engagement
            const updatedEngagements = existingEngagements.map(engagement => 
              engagement.engagement_id === engagementId ? updatedEngagement : engagement
            ).filter((engagement): engagement is Engagement => engagement !== undefined);
            
            // If engagement wasn't found in existing list, add it
            if (!existingEngagements.find(engagement => engagement.engagement_id === engagementId) && updatedEngagement) {
              updatedEngagements.push(updatedEngagement);
            }

            // Set the updated engagements
            campaignData.engagements[battlePlanId] = updatedEngagements;

            return { cachedData: newCachedData };
          });

          return updatedEngagement || null;
        } catch (error) {
          console.warn('Error fetching engagement info (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Fetch my active engagements from API and cache them
      fetchMyActiveEngagements: async () => {
        const { selectedBranch } = get();
        
        if (!selectedBranch) {
          console.warn('No current context set for my active engagements - this may be expected during background fetch');
          throw new Error('No current context set for my active engagements');
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/canvassing/get-my-engagements?only-active=true',
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            },
            'groundforce'
          );

          if (!response.ok) {
            console.warn(`My active engagements fetch failed with status ${response.status} - this may be expected during background fetch`);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const myActiveEngagements = await response.json();
          
          // Cache the my active engagements
          set(() => {
            const newCachedData = { ...get().cachedData };
            
            // Initialize branch if it doesn't exist
            if (!newCachedData[selectedBranch]) {
              newCachedData[selectedBranch] = {
                campaigns: {},
                myActiveEngagements: []
              };
            }
            
            // Store my active engagements directly in the branch
            newCachedData[selectedBranch].myActiveEngagements = myActiveEngagements;

            return { cachedData: newCachedData };
          });

          return myActiveEngagements;
        } catch (error) {
          console.warn('Error fetching my active engagements (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Get my active engagements from cache
      getMyActiveEngagements: () => {
        const { selectedBranch, cachedData } = get();
        
        if (!selectedBranch) {
          return [];
        }

        return cachedData[selectedBranch]?.myActiveEngagements || [];
      },

      // Fetch engagement map details from API and cache them
      fetchEngagementMapDetails: async (engagementIds: string[], battlePlanId: string) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          throw new Error('No current context set for engagement map details');
        }
        
        if (engagementIds.length === 0) {
          return {};
        }
        
        try {
          // Fetch geometry for all engagements in a single call
          const geometryResponse = await authenticatedFetch(
            selectedBranch,
            '/canvassing/get-engagement-geometry',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                battle_plan_id: battlePlanId,
                engagement_ids: engagementIds,
                merge_engagement_geometries: false
              }),
            },
            'groundforce'
          );

          let geometry = null;
          if (geometryResponse.ok) {
            geometry = await geometryResponse.json();
          }

          // Group features by engagement_id from properties
          const engagementFeaturesMap: { [engagementId: string]: any[] } = {};
          
          if (geometry?.features) {
            for (const feature of geometry.features) {
              const engagementId = feature.properties?.engagement_id;
              if (engagementId) {
                if (!engagementFeaturesMap[engagementId]) {
                  engagementFeaturesMap[engagementId] = [];
                }
                engagementFeaturesMap[engagementId].push(feature);
              }
            }
          }

          // Create engagement map details for each engagement
          const engagementMapDetailsMap: { [engagementId: string]: EngagementMapDetails } = {};
          
          for (const engagementId of engagementIds) {
            const features = engagementFeaturesMap[engagementId] || [];
            const engagementGeometry = features.length > 0 ? {
              type: 'FeatureCollection',
              features: features
            } : null;

            const engagementMapDetails: EngagementMapDetails = {
              engagement_id: engagementId,
              geometry: engagementGeometry,
              last_updated: new Date().toISOString()
            };
            
            engagementMapDetailsMap[engagementId] = engagementMapDetails;
          }
          
          // Cache all engagement map details
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            for (const [engagementId, details] of Object.entries(engagementMapDetailsMap)) {
              campaignData.engagementMapDetails[engagementId] = details;
            }

            return { cachedData: newCachedData };
          });

          return engagementMapDetailsMap;
        } catch (error) {
          console.warn('Error fetching engagement map details (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Fetch addresses from API and cache them
      fetchAddressesForEngagement: async (engagementId: string, branchId?: string, campaignId?: string, isGotv?: boolean) => {
        // Use provided branch/campaign IDs or fall back to current context
        let targetBranchId = branchId;
        let targetCampaignId = campaignId;
        
        if (!targetBranchId || !targetCampaignId) {
          const { selectedBranch, selectedCampaign } = get();
          targetBranchId = targetBranchId || selectedBranch || undefined;
          targetCampaignId = targetCampaignId || selectedCampaign || undefined;
        }
        
        if (!targetBranchId || !targetCampaignId) {
          console.warn('No context set for addresses - this may be expected during background fetch');
          throw new Error('No context set for addresses');
        }

        try {
          if (engagementId === '') {
            return [];
          }
          
          // Build query parameters
          const queryParams = new URLSearchParams({
            engagement_id: engagementId,
          });
          
          // Only pass is_gotv=true if explicitly set to true
          if (isGotv === true) {
            queryParams.append('is_gotv', 'true');
          }
          
          const response = await authenticatedFetch(
            targetBranchId,
            `/canvassing/get-addresses-in-engagement?${queryParams.toString()}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            },
            'groundforce'
          );

          if (!response.ok) {
            console.warn(`Address fetch failed with status ${response.status} - this may be expected during background fetch`);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const addresses = await response.json();
          
          // Cache the addresses
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(targetBranchId, targetCampaignId);

            campaignData.addresses[engagementId] = addresses;

            return { cachedData: newCachedData };
          });

          return addresses;
        } catch (error) {
          console.warn('Error fetching addresses (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Fetch street breakdown from API and cache it
      fetchEngagementStreetBreakdown: async (engagementId: string, branchId?: string, campaignId?: string) => {
        // Use provided branch/campaign IDs or fall back to current context
        let targetBranchId = branchId;
        let targetCampaignId = campaignId;
        
        if (!targetBranchId || !targetCampaignId) {
          const { selectedBranch, selectedCampaign } = get();
          targetBranchId = targetBranchId || selectedBranch || undefined;
          targetCampaignId = targetCampaignId || selectedCampaign || undefined;
        }
        
        if (!targetBranchId || !targetCampaignId) {
          console.warn('No context set for street breakdown - this may be expected during background fetch');
          throw new Error('No context set for street breakdown');
        }

        try {
          if (engagementId === '') {
            return [];
          }
          
          const response = await authenticatedFetch(
            targetBranchId,
            `/canvassing/get-engagement-street-breakdown?engagement_id=${encodeURIComponent(engagementId)}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            },
            'groundforce'
          );

          if (!response.ok) {
            console.warn(`Street breakdown fetch failed with status ${response.status} - this may be expected during background fetch`);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const streetBreakdown = await response.json();
          
          // Cache the street breakdown
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(targetBranchId, targetCampaignId);

            // Ensure streetBreakdowns exists (for backward compatibility)
            if (!campaignData.streetBreakdowns) {
              campaignData.streetBreakdowns = {};
            }

            campaignData.streetBreakdowns[engagementId] = streetBreakdown;

            return { cachedData: newCachedData };
          });

          return streetBreakdown;
        } catch (error) {
          console.warn('Error fetching street breakdown (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Fetch postcode slice polygons for an engagement
      fetchPostcodeSlicePolygons: async (engagementId: string, branchId?: string, campaignId?: string) => {
        try {
          const { selectedBranch, selectedCampaign } = get();
          const targetBranchId = branchId || selectedBranch;
          const targetCampaignId = campaignId || selectedCampaign;

          if (!targetBranchId || !targetCampaignId) {
            throw new Error('No branch or campaign context available');
          }

          if (engagementId === '') {
            return null;
          }

          const response = await authenticatedFetch(
            targetBranchId,
            `/canvassing/get-postcode-slice-polygons?engagement_id=${encodeURIComponent(engagementId)}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            },
            'groundforce'
          );

          if (!response.ok) {
            console.warn(`Postcode slice polygons fetch failed with status ${response.status} - this may be expected during background fetch`);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const postcodePolygons = await response.json();
          
          // Cache the postcode polygons
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(targetBranchId, targetCampaignId);

            // Ensure postcodeSlicePolygons exists (for backward compatibility)
            if (!campaignData.postcodeSlicePolygons) {
              campaignData.postcodeSlicePolygons = {};
            }

            campaignData.postcodeSlicePolygons[engagementId] = {
              ...postcodePolygons,
              last_updated: new Date().toISOString()
            };

            return { cachedData: newCachedData };
          });

          return {
            ...postcodePolygons,
            last_updated: new Date().toISOString()
          };
        } catch (error) {
          console.warn('Error fetching postcode slice polygons (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Fetch polling station info for an engagement
      fetchPollingStationInfo: async (engagementId: string, branchId?: string, campaignId?: string) => {
        try {
          const { selectedBranch, selectedCampaign, cachedData } = get();
          const targetBranchId = branchId || selectedBranch;
          const targetCampaignId = campaignId || selectedCampaign;

          if (!targetBranchId || !targetCampaignId) {
            throw new Error('No branch or campaign context available');
          }

          if (engagementId === '') {
            return null;
          }

          // First check if the engagement has a polling_district_id
          // We need to get the engagement from the cached data
          if (!selectedBranch || !selectedCampaign) {
            return null;
          }
          
          const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
          if (!campaignData) {
            return null;
          }
          
          // Find the engagement in any battle plan
          let engagement = null;
          for (const battlePlanId in campaignData.engagements) {
            const engagements = campaignData.engagements[battlePlanId];
            if (engagements) {
              engagement = engagements.find(e => e.engagement_id === engagementId);
              if (engagement) break;
            }
          }

          const pollingDistrictId = engagement?.polling_district_id;
          
          if (!pollingDistrictId) {
            // No polling district ID, don't call the API
            return null;
          }

          // We need to get the polling_station_id from the engagement
          // For now, we'll use the engagement_id as the polling_station_id since that's what the API expects
          

          const response = await authenticatedFetch(
            targetBranchId,
            `/gotv/get-polling-station`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                polling_district_id: pollingDistrictId,
                engagement_id: engagementId
              }),
            },
            'groundforce',
            true // suppressErrors = true
          );

          if (!response.ok) {
            // Some errors can be ignored as per requirements
            console.warn(`Polling station info fetch failed with status ${response.status} - this may be expected for non-active campaigners`);
            return null;
          }

          const pollingStationInfo = await response.json();
          
          // Cache the polling station info
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(targetBranchId, targetCampaignId);

            // Ensure pollingStationInfo exists (for backward compatibility)
            if (!campaignData.pollingStationInfo) {
              campaignData.pollingStationInfo = {};
            }

            campaignData.pollingStationInfo[engagementId] = pollingStationInfo;

            return { cachedData: newCachedData };
          });

          return pollingStationInfo;
        } catch (error) {
          // Some errors can be ignored as per requirements
          console.warn('Error fetching polling station info (may be expected for non-active campaigners):', error);
          return null;
        }
      },

      // Get polling station info for a specific engagement
      getPollingStationInfo: (engagementId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return null;
        }

        return campaignData.pollingStationInfo?.[engagementId] || null;
      },

      // Fetch VI stats for the current campaign
      fetchVIStats: async () => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          throw new Error('No current context set for VI stats');
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/filtered-search/get-stats',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                geo_filter_electoral_division_ids: [selectedCampaign],
                include_vi_stats: true
              }),
            },
            'query' // Not 'groundforce'
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const viStats: VIStats = await response.json();
          
          // Cache the VI stats
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            campaignData.viStats = viStats;

            return { cachedData: newCachedData };
          });

          return viStats;
        } catch (error) {
          console.warn('Error fetching VI stats (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Get VI stats from cache
      getVIStats: () => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return null;
        }

        return campaignData.viStats || null;
      },

      // Cache management for engagement map details
      setEngagementMapDetails: (engagementId: string, details: EngagementMapDetails) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for engagement map details');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Set engagement map details
          campaignData.engagementMapDetails[engagementId] = {
            ...details,
            last_updated: new Date().toISOString()
          };

          return { cachedData: newCachedData };
        });
      },

      getEngagementMapDetails: (engagementId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        return cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.engagementMapDetails[engagementId] || null;
      },

      // Get addresses for a specific engagement
      getAddressesForEngagement: (engagementId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return [];
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return [];
        }

        const addresses = campaignData.addresses[engagementId];
        if (!addresses) {
          return [];
        }

        // Merge offline updates with base addresses and their voters
        return addresses.map((address: Address) => {
          const addressUpdates = campaignData.addressUpdates[address.address_id] || {};
          
          // Merge voter updates for each voter in the address
          const updatedVoters = address.voters.map((voter: Voter) => {
            const voterUpdates = campaignData.voterUpdates[voter.voter_id] || {};
            return { ...voter, ...voterUpdates };
          });
          
          // Check if any voters have updates and mark address as visited if so
          const hasVoterUpdates = address.voters.some((voter: Voter) => {
            return campaignData.voterUpdates[voter.voter_id] && 
                   Object.keys(campaignData.voterUpdates[voter.voter_id] || {}).length > 0;
          });
          
          return { 
            ...address, 
            ...addressUpdates,
            visited: address.visited || hasVoterUpdates,
            voters: updatedVoters
          };
        });
      },

      // Get street breakdown for a specific engagement
      getEngagementStreetBreakdown: (engagementId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return [];
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return [];
        }

        return campaignData.streetBreakdowns?.[engagementId] || [];
      },

      // Get postcode slice polygons for a specific engagement
      getPostcodeSlicePolygons: (engagementId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return null;
        }

        return campaignData.postcodeSlicePolygons?.[engagementId] || null;
      },

      // Set addresses for a specific engagement
      setAddressesForEngagement: (engagementId: string, addresses: Address[]) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for addresses');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Set addresses for this engagement (store raw server data)
          campaignData.addresses[engagementId] = addresses;

          return { cachedData: newCachedData };
        });
      },

      // Update voter data for current context
      updateVoterData: (voterId: string, updates: Partial<Voter>) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for voter update');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Get current voter updates and ensure they have timestamps
          const currentVoterUpdates = campaignData.voterUpdates[voterId] || {};
          const currentUpdates = currentVoterUpdates.updates || [];
          
          // Add timestamp to the new update
          const timestampedUpdate = {
            ...updates,
            _timestamp: Date.now(),
            _sequence: currentUpdates.length
          };
          
          // Add new update to the sequence
          const updatedSequence = [...currentUpdates, timestampedUpdate];
          
          // Apply all updates in chronological order to get the final state
          const finalState = updatedSequence.reduce((acc, update) => {
            const { _timestamp, _sequence, ...updateData } = update;
            return { ...acc, ...updateData };
          }, {});
          
          // Store both the final state and the update sequence
          campaignData.voterUpdates[voterId] = {
            ...finalState,
            updates: updatedSequence
          };

          // Also update the underlying address data to reflect changes immediately
          // Find which address contains this voter and update it
          for (const engagementId in campaignData.addresses) {
            const addresses = campaignData.addresses[engagementId];
            if (!addresses) continue;
            
            // Find the address and voter index
            let addressIndex = -1;
            let voterIndex = -1;
            let foundAddress: Address | undefined;
            for (let i = 0; i < addresses.length; i++) {
              const addr = addresses[i];
              if (!addr) continue;
              const vIndex = addr.voters.findIndex((v: Voter) => v.voter_id === voterId);
              if (vIndex !== -1) {
                addressIndex = i;
                voterIndex = vIndex;
                foundAddress = addr;
                break;
              }
            }
            
            if (addressIndex !== -1 && voterIndex !== -1 && foundAddress) {
              // Filter out undefined values to satisfy exactOptionalPropertyTypes
              const filteredFinalState = Object.fromEntries(
                Object.entries(finalState).filter(([_, value]) => value !== undefined)
              );
              
              // Create new arrays to ensure Zustand detects the change
              const updatedAddresses = [...addresses];
              const updatedVoters = [...foundAddress.voters];
              updatedVoters[voterIndex] = { ...foundAddress.voters[voterIndex], ...filteredFinalState } as Voter;
              
              // Mark address as visited if any voter has updates
              const hasVoterUpdates = updatedVoters.some((v: Voter) => {
                return campaignData.voterUpdates[v.voter_id] &&
                       Object.keys(campaignData.voterUpdates[v.voter_id] || {}).length > 0;
              });
              
              // Create new address object with updated voters
              updatedAddresses[addressIndex] = {
                ...foundAddress,
                voters: updatedVoters,
                visited: foundAddress.visited || hasVoterUpdates
              } as Address;
              
              campaignData.addresses[engagementId] = updatedAddresses;
              break;
            }
          }

          return { cachedData: newCachedData };
        });
      },

      // Update address data for current context
      updateAddressData: (addressId: string, updates: Partial<Address>) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for address update');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Get current address updates and ensure they have timestamps
          const currentAddressUpdates = campaignData.addressUpdates[addressId] || {};
          const currentUpdates = currentAddressUpdates.updates || [];
          
          // Add timestamp to the new update
          const timestampedUpdate = {
            ...updates,
            _timestamp: Date.now(),
            _sequence: currentUpdates.length
          };
          
          // Add new update to the sequence
          const updatedSequence = [...currentUpdates, timestampedUpdate];
          
          // Apply all updates in chronological order to get the final state
          const finalState = updatedSequence.reduce((acc, update) => {
            const { _timestamp, _sequence, ...updateData } = update;
            return { ...acc, ...updateData };
          }, {});
          
          // Store both the final state and the update sequence
          campaignData.addressUpdates[addressId] = {
            ...finalState,
            updates: updatedSequence
          };

          // Also update the underlying address data to reflect changes immediately
          for (const engagementId in campaignData.addresses) {
            const addresses = campaignData.addresses[engagementId];
            if (!addresses) continue;
            const addressIndex = addresses.findIndex((addr: Address) => addr.address_id === addressId);
            if (addressIndex !== -1) {
              // Update the address data
              // Filter out undefined values to satisfy exactOptionalPropertyTypes
              const filteredFinalState = Object.fromEntries(
                Object.entries(finalState).filter(([_, value]) => value !== undefined)
              );
              
              // Create a new array reference so Zustand detects the change
              const updatedAddresses = [...addresses];
              updatedAddresses[addressIndex] = { ...addresses[addressIndex], ...filteredFinalState } as Address;
              campaignData.addresses[engagementId] = updatedAddresses;
              break;
            }
          }

          return { cachedData: newCachedData };
        });
      },

      // Update telling data for current context
      updateTellingData: (voterId: string, updates: any) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No current context set for telling update');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          // Ensure tellingUpdates is an object (fix for existing data that might have false)
          if (!campaignData.tellingUpdates || typeof campaignData.tellingUpdates !== 'object') {
            campaignData.tellingUpdates = {};
          }
          
          // Get current telling updates and ensure they have timestamps
          const currentTellingUpdates = campaignData.tellingUpdates[voterId] || {};
          const currentUpdates = currentTellingUpdates.updates || [];
          
          // Add timestamp to the new update
          const timestampedUpdate = {
            ...updates,
            _timestamp: Date.now(),
            _sequence: currentUpdates.length
          };
          
          // Add new update to the sequence
          const updatedSequence = [...currentUpdates, timestampedUpdate];
          
          // Apply all updates in chronological order to get the final state
          const finalState = updatedSequence.reduce((acc, update) => {
            const { _timestamp, _sequence, ...updateData } = update;
            return { ...acc, ...updateData };
          }, {});
          
          // Store both the final state and the update sequence
          campaignData.tellingUpdates[voterId] = {
            ...finalState,
            updates: updatedSequence
          };

          // Also update the underlying engagement-based telling voters data to reflect changes immediately
          // This will trigger useBackgroundTellingVotersByEngagements to be responsive
          if (finalState.engagement_id) {
            // Find all cached engagement combinations that include this engagement
            const engagementId = finalState.engagement_id;
            const cachedKeys = Object.keys(campaignData.tellingVotersByEngagements || {});
            
            for (const engagementIdsKey of cachedKeys) {
              const engagementIds = engagementIdsKey.split(',');
              if (engagementIds.includes(engagementId)) {
                const tellingVotersData = campaignData.tellingVotersByEngagements[engagementIdsKey];
                if (tellingVotersData?.voters) {
                  const voterIndex = tellingVotersData.voters.findIndex((voter: any) => voter.voter_id === voterId);
                  if (voterIndex !== -1) {
                    // Update the voter data in the engagement-based telling voters list
                    tellingVotersData.voters[voterIndex] = { 
                      ...tellingVotersData.voters[voterIndex], 
                      ...finalState 
                    };
                  }
                }
              }
            }
          }

          return { cachedData: newCachedData };
        });
      },

      // Get voter updates for current context
      getVoterUpdates: (voterId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const voterUpdates = cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.voterUpdates[voterId];
        if (!voterUpdates) {
          return null;
        }

        // Return the final state without the internal update sequence
        const { updates, ...finalState } = voterUpdates;
        return finalState;
      },

      // Get address updates for current context
      getAddressUpdates: (addressId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const addressUpdates = cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.addressUpdates[addressId];
        if (!addressUpdates) {
          return null;
        }

        // Return the final state without the internal update sequence
        const { updates, ...finalState } = addressUpdates;
        return finalState;
      },

      // Get telling updates for current context
      getTellingUpdates: (voterId: string) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const tellingUpdates = cachedData[selectedBranch]?.campaigns?.[selectedCampaign]?.tellingUpdates[voterId];
        if (!tellingUpdates) {
          return null;
        }

        // Return the final state without the internal update sequence
        const { updates, ...finalState } = tellingUpdates;
        return finalState;
      },

      // Clear current context
      clearCurrentContext: () => {
        set({ selectedBranch: null, selectedCampaign: null });
      },

      // Clear all data (useful for logout)
      clearAllData: () => {
        set({ selectedBranch: null, selectedCampaign: null, cachedData: {} });
      },

      // Clear all data for a specific engagement (useful when leaving an engagement)
      clearEngagementData: (engagementId: string) => {
        const { selectedBranch, selectedCampaign } = get();
        if (!selectedBranch || !selectedCampaign) return;

        set((state) => {
          const newCachedData = { ...state.cachedData };
          const campaignData = newCachedData[selectedBranch]?.campaigns?.[selectedCampaign];
          
          if (!campaignData) return { cachedData: newCachedData };

          // Clear engagement-specific data
          if (campaignData.addresses) {
            delete campaignData.addresses[engagementId];
          }
          if (campaignData.postcodeSlicePolygons) {
            delete campaignData.postcodeSlicePolygons[engagementId];
          }
          // Clear engagement-based telling voters data for this engagement
          if (campaignData.tellingVotersByEngagements) {
            const keysToDelete: string[] = [];
            for (const engagementIdsKey of Object.keys(campaignData.tellingVotersByEngagements)) {
              const engagementIds = engagementIdsKey.split(',');
              if (engagementIds.includes(engagementId)) {
                keysToDelete.push(engagementIdsKey);
              }
            }
            keysToDelete.forEach(key => delete campaignData.tellingVotersByEngagements[key]);
          }

          const oneHourAgo = Date.now() - (60 * 60 * 1000);

          // Clear interaction IDs for addresses in this engagement
          if (campaignData.interactionIds) {
            for (const addressId in campaignData.interactionIds) {
              const interactionData = campaignData.interactionIds[addressId];
              if (interactionData?.timestamp && interactionData.timestamp < oneHourAgo) {
                delete campaignData.interactionIds[addressId];
              }
            }
          }

          // Clear telling interaction IDs for this engagement
          if (campaignData.tellingInteractionIds) {
            for (const key in campaignData.tellingInteractionIds) {
              const interactionData = campaignData.tellingInteractionIds[key];
              if (interactionData?.timestamp && interactionData.timestamp < oneHourAgo) {
                delete campaignData.interactionIds[key];
              }
            }
          }

          return { cachedData: newCachedData };
        });
      },

      // Store interaction ID for an address
      storeInteractionId: (addressId: string, interactionId: string, notIn: boolean) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No branch or campaign context available for storing interaction ID');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);
          
          // Store the interaction ID
          campaignData.interactionIds[addressId] = {
            interactionId,
            timestamp: Date.now(),
            notIn
          };
          
          return { cachedData: newCachedData };
        });
      },

      // Store telling interaction ID for an engagement and voter
      storeTellingInteractionId: (engagementId: string, interactionId: string, voterId: string) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          console.warn('No branch or campaign context available for storing telling interaction ID');
          return;
        }

        set(() => {
          const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

          if (!campaignData.tellingInteractionIds) {
            campaignData.tellingInteractionIds = {};
          }
          // Create composite key for multiple voters per engagement
          const compositeKey = `${engagementId}:${voterId}`;
          
          // Store the telling interaction ID
          campaignData.tellingInteractionIds[compositeKey] = {
            interactionId,
            timestamp: Date.now(),
            engagementId,
            voterId
          };
          
          return { cachedData: newCachedData };
        });
      },

      // Fetch telling voters by engagement IDs (efficient for polling station views)
      fetchTellingVotersByEngagements: async (engagementIds: string[] | null) => {
        const { selectedBranch, selectedCampaign } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          throw new Error('No current context set for telling voters');
        }

        if (!engagementIds || engagementIds.length === 0) {
          return null;
        }

        try {
          const response = await authenticatedFetch(
            selectedBranch,
            '/telling/get-voters-in-telling-engagement',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  engagement_ids: engagementIds,
              }),
            },
            'groundforce'
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const votersData = await response.json();
          
          // Cache the telling voters by engagement IDs
          set(() => {
            const { newCachedData, campaignData } = initializeCampaign(selectedBranch, selectedCampaign);

            // Ensure tellingVotersByEngagements exists
            if (!campaignData.tellingVotersByEngagements) {
              campaignData.tellingVotersByEngagements = {};
            }

            // Create composite key from sorted engagement IDs
            const engagementIdsKey = engagementIds.sort().join(',');

            // Store telling voters for this engagement combination
            campaignData.tellingVotersByEngagements[engagementIdsKey] = {
              voters: votersData,
              last_updated: new Date().toISOString()
            };

            return { cachedData: newCachedData };
          });

          return votersData;
        } catch (error) {
          console.warn('Error fetching telling voters by engagements (may be expected during background fetch):', error);
          throw error;
        }
      },

      // Get telling voters by engagement IDs from cache
      getTellingVotersByEngagements: (engagementIds: string[]) => {
        const { selectedBranch, selectedCampaign, cachedData } = get();
        
        if (!selectedBranch || !selectedCampaign) {
          return null;
        }

        const campaignData = cachedData[selectedBranch]?.campaigns?.[selectedCampaign];
        if (!campaignData) {
          return null;
        }

        // Create composite key from sorted engagement IDs to match the storage format
        const engagementIdsKey = engagementIds.sort().join(',');
        const tellingVotersData = campaignData.tellingVotersByEngagements?.[engagementIdsKey];
        return tellingVotersData?.voters || null;
      },

    };
    },
    {
      name: 'campaign-storage',
      storage: createJSONStorage(() => createPlatformStorage()),
      partialize: (state: CampaignStore) => ({
        selectedBranch: state.selectedBranch,
        selectedCampaign: state.selectedCampaign,
        cachedData: state.cachedData,
      }),
    }
  )
);
